import React, { useEffect, useRef, useState } from 'react'
import { BpButton } from '../../../shared/components/buttons/bp-button/BpButton'
import { hideModal, setModal, showAlert } from '../../../app/store/reducers/AppSlice'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../../../app/store/hooks/Redux'
import { Notify } from '../../../shared/components/text/notify/Notify'
import { BpSelect } from '../../../shared/components/forms/select/BpSelect'
import { IconPlus } from '../../../shared/components/icons/IconPlus'
import { IconTrash } from '../../../shared/components/icons/IconTrash'
import { Preloader } from '../../../shared/components/cards/preloader/Preloader'
import {
  useGetNotificationScheduleQuery,
  usePostNotificationSaveScheduleMutation,
} from '../../../app/api'
import { handleCommonResponse } from '../../../shared/lib/helpers/ErrorHandlingHelplers'
import {
  AlertType,
  DEFAULT_SCHEDULE,
  DEFAULT_SEND_TIME,
  DeliveryMode,
  duplicateSendIndexes,
  Frequency,
  FREQUENCIES,
  isMultiple,
  isWeekly,
  matchesTimezone,
  nextFreeTime,
  RecipientChannel,
  Send,
  sendsForFrequency,
  TIME_OPTIONS,
  WEEKDAYS,
} from './model/alerting'
import { useTimezones } from './model/useTimezones'
import { RecipientField } from './ui/RecipientField'
import { RecipientChips } from './ui/RecipientChips'

const DELIVERY_MODES: DeliveryMode[] = ['instant', 'scheduled']

type SendRow = Send & { id: number }

interface IAlertingScheduleModalProps {
  projectId: number
  type: AlertType
}

/** Открыть модалку расписания для типа нарушений */
export const openAlertingSchedule = (projectId: number, type: AlertType) =>
  setModal({
    children: <AlertingScheduleModal projectId={projectId} type={type} />,
    size: 'lg',
    width: 660,
  })

export const AlertingScheduleModal = ({ projectId, type }: IAlertingScheduleModalProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const nextRowId = useRef(0)
  const [initialized, setInitialized] = useState(false)
  const [mode, setMode] = useState<DeliveryMode>('instant')
  const [frequency, setFrequency] = useState<Frequency | null>(null)
  const [timezone, setTimezone] = useState<string | null>(null)
  const [sends, setSends] = useState<SendRow[]>([])
  const [recipients, setRecipients] = useState<Record<RecipientChannel, string[]>>({
    email: [],
    tg: [],
  })
  const [timezoneSearch, setTimezoneSearch] = useState('')
  const timezones = useTimezones()

  const { data, isFetching } = useGetNotificationScheduleQuery(
    { projectId, type },
    { refetchOnMountOrArgChange: true },
  )
  const [saveSchedule, { isLoading: saving }] = usePostNotificationSaveScheduleMutation()

  const toRows = (list: Send[]): SendRow[] =>
    list.map((send) => ({ ...send, id: nextRowId.current++ }))

  useEffect(() => {
    if (initialized || isFetching || !data?.data) return

    const schedule = data.data

    setMode(schedule.mode)
    setFrequency(schedule.frequency)
    setTimezone(schedule.timezone)
    setSends(toRows(schedule.sends))
    // последний добавленный — первым
    setRecipients({
      email: [...schedule.recipients.email].reverse(),
      tg: [...schedule.recipients.telegram].reverse(),
    })
    setInitialized(true)
  }, [isFetching, data])

  const changeMode = (value: DeliveryMode) => {
    setMode(value)

    // расписание ещё не составляли — подставляем пресет, чтобы юзер правил готовое
    if (value === 'scheduled' && !frequency) {
      setFrequency(DEFAULT_SCHEDULE.frequency)
      setTimezone(timezone ?? DEFAULT_SCHEDULE.timezone)
      setSends(toRows(DEFAULT_SCHEDULE.sends))
    }
  }

  // Очищенное в селекте значение сбрасывается и в данных — иначе поле пустое, а Save активна
  const changeFrequency = (value: Frequency | '') => {
    if (value === (frequency ?? '')) return

    if (!value) {
      setFrequency(null)
      return
    }

    setFrequency(value)
    setSends(toRows(sendsForFrequency(value, sends)))
  }

  const updateSend = (id: number, patch: Partial<Send>) => {
    const row = sends.find((send) => send.id === id)

    if (!row || Object.entries(patch).every(([key, value]) => row[key as keyof Send] === value))
      return

    setSends(sends.map((send) => (send.id === id ? { ...send, ...patch } : send)))
  }

  const addSend = () => {
    setSends([
      ...sends,
      ...toRows([
        isWeekly(frequency)
          ? { weekday: null, time: DEFAULT_SEND_TIME }
          : { time: nextFreeTime(sends) },
      ]),
    ])
  }

  const addRecipient = (channel: RecipientChannel, value: string) =>
    setRecipients({
      ...recipients,
      [channel]: [value, ...recipients[channel].filter((item) => item !== value)],
    })

  const removeRecipient = (channel: RecipientChannel, value: string) =>
    setRecipients({
      ...recipients,
      [channel]: recipients[channel].filter((item) => item !== value),
    })

  const weekly = isWeekly(frequency)
  const duplicates = mode === 'scheduled' ? duplicateSendIndexes(frequency, sends) : []
  const hasRecipients = !!recipients.email.length || !!recipients.tg.length
  const scheduleValid =
    mode === 'instant' ||
    (!!frequency &&
      !!timezone &&
      !!sends.length &&
      !duplicates.length &&
      sends.every((send) => !!send.time && (!weekly || !!send.weekday)))
  const canSave = initialized && scheduleValid && hasRecipients

  const save = async () => {
    const response = await saveSchedule({
      notificationScheduleForm: {
        project_id: projectId,
        type,
        mode,
        ...(mode === 'scheduled' && {
          frequency,
          timezone,
          sends: sends.map(({ weekday, time }) => (weekly ? { weekday, time } : { time })),
        }),
        recipients: { email: recipients.email, telegram: recipients.tg },
      },
    })
      .unwrap()
      .catch(() => ({ success: false, errors: { save: [t('notifications.failedToSave')] } }))

    if (response.success) {
      dispatch(
        showAlert({
          message: t('components.alertingSchedule.saved'),
          type: 'success',
          timeout: 3000,
        }),
      )
      dispatch(hideModal())
    } else {
      handleCommonResponse(response, dispatch)
    }
  }

  // без поиска — основные зоны (и выбранная, если она не из них), с поиском — все
  const timezoneOptions = timezoneSearch
    ? timezones.all.filter(({ text }) => matchesTimezone(text, timezoneSearch))
    : [
        ...timezones.all.filter(
          ({ value }) => value === timezone && !timezones.main.some((main) => main.value === value),
        ),
        ...timezones.main,
      ]

  const frequencyOptions = FREQUENCIES.map((value) => ({
    value,
    text: t(`components.alertingSchedule.frequencies.${value}`),
  }))

  const weekdayOptions = WEEKDAYS.map((value) => ({
    value: String(value),
    text: t(`components.alertingSchedule.weekdays.${value}`),
  }))

  return (
    <div className={'flex flex-col overflow-hidden'}>
      <div
        className={
          'flex min-h-[55px] w-full flex-col justify-center border-b-[3px] border-b-[#00326D]/[.02] px-[24px] py-[12px]'
        }
      >
        <h3 className={'text-[20px] font-medium'}>{t('components.alertingSchedule.title')}</h3>
        <p className={'text-[13px] text-[#8E7CA7]'}>
          {t('components.alertingSchedule.projectOnly')}
        </p>
      </div>
      {!initialized ? (
        <div className={'relative h-[240px]'}>
          <Preloader />
        </div>
      ) : (
        <div className={'flex flex-1 flex-col gap-[40px] overflow-auto p-[24px]'}>
          <div className={'flex flex-col gap-[16px]'}>
            <div className={'flex flex-col gap-[8px]'}>
              <h2 className={'text-[18px]'}>{t('components.alertingSchedule.deliveryMode')}</h2>
              <div className={'flex flex-col gap-[8px] md:flex-row'}>
                {DELIVERY_MODES.map((value) => (
                  <label
                    key={value}
                    className={`flex w-full cursor-pointer gap-[8px] rounded-[8px] border px-[16px] py-[12px] ${
                      mode === value
                        ? 'border-[#0E63D8] bg-[#EBF4FF]'
                        : 'border-transparent bg-[#F7F6F9]'
                    }`}
                  >
                    <input
                      hidden
                      checked={mode === value}
                      name={'delivery-mode'}
                      type={'radio'}
                      value={value}
                      onChange={() => changeMode(value)}
                    />
                    <div>
                      <div
                        className={`flex h-[20px] w-[20px] items-center justify-center rounded-full border bg-white ${
                          mode === value ? 'border-[#A059FF]' : 'border-[#A89ABB]'
                        }`}
                      >
                        {mode === value && (
                          <div className={'h-[10px] w-[10px] rounded-full bg-[#A059FF]'}></div>
                        )}
                      </div>
                    </div>
                    <div className={'flex flex-col gap-[4px]'}>
                      <div className={'font-medium text-focus-500'}>
                        {t(`components.alertingSchedule.modes.${value}.title`)}
                      </div>
                      <p className={'text-[14px] text-[#8E7CA7]'}>
                        {t(`components.alertingSchedule.modes.${value}.description`)}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            {mode === 'instant' ? (
              <Notify>{t('components.alertingSchedule.instantWarning')}</Notify>
            ) : (
              <div className={'flex flex-col gap-[12px]'}>
                <div className={'flex flex-col gap-[16px] md:flex-row'}>
                  <div className={'w-full min-w-0'}>
                    {/* defaultValue прячет крестик очистки и не даёт снять выбор повторным кликом:
                        пустого значения у полей расписания не бывает */}
                    <BpSelect
                      defaultValue={frequency ?? ''}
                      label={t('components.alertingSchedule.frequency')}
                      options={frequencyOptions}
                      showSearch={false}
                      value={frequency ?? ''}
                      valueColor={'text-focus-500'}
                      onChange={(e) => changeFrequency(e.target.value as Frequency | '')}
                    />
                  </div>
                  <div className={'w-full min-w-0'}>
                    <BpSelect
                      defaultValue={timezone ?? ''}
                      label={t('components.alertingSchedule.timezone')}
                      loading={timezones.loading}
                      options={timezoneOptions}
                      value={timezone ?? ''}
                      valueColor={'text-focus-500'}
                      onChange={(e) => setTimezone(e.target.value || null)}
                      onSearch={setTimezoneSearch}
                    />
                  </div>
                </div>
                <div>
                  <div
                    className={
                      'mb-[2px] flex flex-wrap items-center gap-x-[4px] text-[14px] font-medium leading-[24px] text-focus-500'
                    }
                  >
                    {t(
                      weekly
                        ? 'components.alertingSchedule.sendDays'
                        : 'components.alertingSchedule.sendTimes',
                    )}
                  </div>
                  <p className={'text-[13px] text-[#8E7CA7]'}>
                    {t('components.alertingSchedule.nextSendHint')}
                  </p>
                </div>
                <div className={'flex flex-col gap-[8px]'}>
                  {sends.map((send, index) => (
                    <div key={send.id} className={'flex flex-col gap-[4px]'}>
                      <div className={'flex items-center gap-[8px]'}>
                        {weekly ? (
                          <div className={'min-w-0 flex-1 md:w-[160px] md:flex-none'}>
                            <BpSelect
                              defaultValue={send.weekday ? String(send.weekday) : ''}
                              options={weekdayOptions}
                              placeholder={t('components.alertingSchedule.selectDay')}
                              showSearch={false}
                              value={send.weekday ? String(send.weekday) : ''}
                              valueColor={'text-focus-500'}
                              onChange={(e) =>
                                updateSend(send.id, { weekday: Number(e.target.value) || null })
                              }
                            />
                          </div>
                        ) : (
                          <div className={'w-[80px] shrink-0 text-[13px] text-[#8E7CA7]'}>
                            {isMultiple(frequency)
                              ? t('components.alertingSchedule.sendNumber', { number: index + 1 })
                              : t('components.alertingSchedule.at')}
                          </div>
                        )}
                        <div className={'min-w-0 flex-1 md:w-[160px] md:flex-none'}>
                          <BpSelect
                            defaultValue={send.time}
                            options={TIME_OPTIONS}
                            showSearch={false}
                            value={send.time}
                            valueColor={'text-focus-500'}
                            onChange={(e) => updateSend(send.id, { time: e.target.value })}
                          />
                        </div>
                        <button
                          className={
                            'w-[16px] shrink-0 disabled:cursor-not-allowed disabled:opacity-40'
                          }
                          disabled={sends.length === 1}
                          type={'button'}
                          onClick={() => setSends(sends.filter(({ id }) => id !== send.id))}
                        >
                          <IconTrash color={'#6684A7'} />
                        </button>
                      </div>
                      {duplicates.includes(index) && (
                        <p className={'text-[13px] text-[#DC2F02]'}>
                          {t(
                            weekly
                              ? 'components.alertingSchedule.duplicateDayTime'
                              : 'components.alertingSchedule.duplicateTime',
                          )}
                        </p>
                      )}
                    </div>
                  ))}
                  {isMultiple(frequency) && (
                    <div>
                      <button
                        className={
                          'flex w-fit cursor-pointer items-center gap-[4px] text-[14px] leading-[20px] text-focus'
                        }
                        type={'button'}
                        onClick={addSend}
                      >
                        <IconPlus className={'w-[12px]'} color={'#0E63D8'} />
                        {t(
                          weekly
                            ? 'components.alertingSchedule.addSendDay'
                            : 'components.alertingSchedule.addSendTime',
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className={'flex flex-col gap-[8px]'}>
            <h2 className={'text-[18px]'}>
              {t(`components.alertingSchedule.recipientsFor.${type}`)}
            </h2>
            <div className={'flex flex-col gap-[8px]'}>
              {(['email', 'tg'] as RecipientChannel[]).map((channel) => (
                <div
                  key={channel}
                  className={
                    'flex flex-col gap-[8px] rounded-[8px] bg-focus/[.03] py-[12px] px-[16px]'
                  }
                >
                  <RecipientField
                    channel={channel}
                    onAdd={(value) => addRecipient(channel, value)}
                  />
                  <RecipientChips
                    items={recipients[channel].map((value) => ({ value }))}
                    removeTooltip={t('components.alertingSchedule.removeFromAlerts')}
                    onRemove={(value) => removeRecipient(channel, value)}
                  />
                </div>
              ))}
            </div>
            {!hasRecipients && (
              <p className={'text-[13px] text-[#DC2F02]'}>
                {t('components.alertingSchedule.recipientRequired')}
              </p>
            )}
            <p className={'text-[13px] text-[#8E7CA7]'}>
              {t('components.alertingSchedule.sharedRecipientsHint')}
            </p>
          </div>
        </div>
      )}
      <div
        className={
          'flex w-full gap-[12px] border-t-[3px] border-t-[#00326D]/[.02] px-[24px] py-[24px]'
        }
      >
        <BpButton
          className={'uppercase'}
          disabled={!canSave}
          loading={saving}
          type={'button'}
          onClick={save}
        >
          {t('buttons.saveChanges')}
        </BpButton>
        <BpButton theme='transparent' type='button' onClick={() => dispatch(hideModal())}>
          {t('buttons.cancel')}
        </BpButton>
      </div>
    </div>
  )
}
